/**
 * Message and status primitives — a tinted result box and a connection-state pill.
 *
 * WHY INLINE STYLES: every colour here is a theme CSS var (`--success`, `--warning`,
 * `--destructive`, `--info`), and Tailwind v3 silently DROPS an opacity modifier on a
 * colour defined as a bare `var()` — `bg-success/15` compiles to nothing at all, which is
 * how a tinted box ends up as coloured text on no background. `color-mix()` on the var
 * cannot be dropped and tints correctly in both themes. Same approach as shared StatusTag.
 *
 * Using these instead of `bg-green-50 dark:bg-green-900/20` is also what keeps a screen on the
 * product's palette: a deployment that rebrands the theme rebrands these too.
 *
 * Lives in the shared package rather than beside Settings because three screens now show this
 * same kind of verdict — the local Settings panels, the camera CSV import, and the cloud's
 * per-site Settings — and a second copy of a colour rule is a second copy that drifts.
 */
import type { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export type Tone = 'success' | 'warning' | 'danger' | 'info';

export const TONE_VAR: Record<Tone, string> = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--destructive)',
  info: 'var(--info)',
};

const TONE_ICON = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  danger: XCircleIcon,
  info: InformationCircleIcon,
};

/** A tinted message block — the result of an action, or a caveat about one. */
export function Callout({
  tone = 'info',
  icon = true,
  children,
  className = '',
}: {
  tone?: Tone;
  /** Off for a block whose content carries its own heading and list. */
  icon?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const color = TONE_VAR[tone];
  const Icon = TONE_ICON[tone];
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[12px] leading-relaxed ${className}`}
      style={{
        color,
        borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      }}
    >
      {icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" />}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** A state pill with a dot — for a connection that is live, trying, or down. */
export function StatusPill({
  tone,
  children,
  pulse = false,
}: {
  tone: Tone | 'neutral';
  children: ReactNode;
  pulse?: boolean;
}) {
  const color = tone === 'neutral' ? 'var(--muted-foreground)' : TONE_VAR[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}
    >
      <span
        className={`h-2 w-2 rounded-full ${pulse ? 'animate-pulse' : ''}`}
        style={{ backgroundColor: color }}
      />
      {children}
    </span>
  );
}
